import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useLinkInterception} from '@github-ui/use-link-interception'
import type {Icon} from '@primer/octicons-react'
import {Timeline} from '@primer/react'
import {Octicon} from '@primer/react/deprecated'
import {useSlots} from '@primer/react/experimental'
import {clsx} from 'clsx'
import type React from 'react'
import {useRef} from 'react'
import {graphql, useFragment} from 'react-relay'

import type {TimelineRowEventActor$data, TimelineRowEventActor$key} from './__generated__/TimelineRowEventActor.graphql'
import {Ago} from './Ago'
import {EventActor} from './EventActor'
import styles from './row.module.css'
import timelineRowStyles from './TimelineRow.module.css'

type TimelineRowType = {
  actor: TimelineRowEventActor$key | null | undefined
  highlighted: boolean
  createdAt: string
  deepLinkUrl: string
  onLinkClick?: (event: MouseEvent) => void
  showAgoTimestamp?: boolean
  showActorName?: boolean
  refAttribute?: React.RefObject<HTMLDivElement | null>
  children: React.ReactNode
  leadingIcon: Icon
  iconColoring?: {
    color?: string
    backgroundColor?: string
  }
  fillRow?: boolean
}

type RowInternalType = Omit<TimelineRowType, 'children' | 'actor'> & {
  slots: RowSlots
  actor: TimelineRowEventActor$key
}

type EventRowSlotsConfig = {
  trailing: typeof Trailing
  secondary: typeof Secondary
  main: typeof Main
}

type RowSlots = ReturnType<typeof useSlots<EventRowSlotsConfig>>

const Row = ({actor, children, ...props}: TimelineRowType) => {
  const slots = useSlots<EventRowSlotsConfig>(children, {
    trailing: Trailing,
    secondary: Secondary,
    main: Main,
  })

  if (actor) return <RowInternal actor={actor} slots={slots} {...props} />

  return <RowBase actorData={null} slots={slots} {...props} />
}

const RowInternal = ({actor, ...props}: RowInternalType) => {
  const actorData = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment TimelineRowEventActor on Actor {
        ...EventActor
      }
    `,
    actor,
  )

  return <RowBase {...props} actorData={actorData} />
}

type RowBaseType = Omit<TimelineRowType, 'children' | 'actor'> & {
  slots: RowSlots
  actorData?: TimelineRowEventActor$data | null
}

export const RowBase = ({
  showActorName = true,
  showAgoTimestamp = true,
  highlighted,
  createdAt,
  deepLinkUrl,
  onLinkClick,
  refAttribute,
  leadingIcon,
  iconColoring,
  slots,
  actorData,
  fillRow,
}: RowBaseType) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  useLinkInterception({htmlContainerRef: containerRef, onLinkClick, openLinksInNewTab: false})

  return (
    <Timeline.Item
      ref={highlighted ? refAttribute : null}
      className={clsx(
        timelineRowStyles.Timeline_Item,
        isFeatureEnabled('issue_inline_avatars') && timelineRowStyles.Timeline_Item_inlineAvatars,
      )}
      style={{'--timelineBadge-bgColor': iconColoring?.backgroundColor}}
    >
      <Timeline.Badge className={clsx(highlighted && timelineRowStyles.highlightedBadge)}>
        {leadingIcon && (
          <span style={{color: iconColoring?.color}} className="d-flex">
            <Octicon icon={leadingIcon} className={timelineRowStyles.Octicon} />
          </span>
        )}
      </Timeline.Badge>
      {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
      <Timeline.Body ref={containerRef} className={clsx('TimelineBody', styles.body, fillRow && styles.fillRow)}>
        <div className={styles.timelineBodyRowContainer}>
          <div className={styles.timelineBodyContent}>
            <EventActor actor={actorData || null} showAvatarOnly={!showActorName} />
            {slots[0].main}
            {showAgoTimestamp && <Ago timestamp={new Date(createdAt)} linkUrl={deepLinkUrl} />}
          </div>
          <div className={styles.timelineBodyTrailingContent}>{slots[0].trailing}</div>
        </div>
        <div>{slots[0].secondary}</div>
      </Timeline.Body>
    </Timeline.Item>
  )
}

function Trailing(props: React.PropsWithChildren) {
  return <div data-trailing>{props.children}</div>
}

function Secondary(props: React.PropsWithChildren) {
  return <div data-secondary>{props.children}</div>
}

function Main(props: React.PropsWithChildren) {
  return <>{props.children}</>
}

export const TimelineRow = Object.assign(Row, {
  Trailing,
  Secondary,
  Main,
})
